import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";

// Renders assistant markdown WITH LaTeX math (KaTeX). The tutor emits math as
// \( ... \) / \[ ... \]; convert those to $ / $$ so remark-math can typeset them.
function normalizeMath(src) {
  let s = src == null ? "" : String(src);
  s = s.replace(/\\\[([\s\S]+?)\\\]/g, (_, e) => `$$${e}$$`);
  s = s.replace(/\\\(([\s\S]+?)\\\)/g, (_, e) => `$${e}$`);
  return s;
}

export function MarkdownMath({ content, className = "" }) {
  return (
    <div className={`text-sm leading-relaxed ${className}`} data-testid="markdown-math-message">
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[[rehypeKatex, { throwOnError: false, strict: false }]]}
        components={{
          h1: ({ node, ...p }) => <h1 className="text-base font-bold text-indigo-700 dark:text-indigo-300 mt-3 mb-1.5 first:mt-0" style={{ fontFamily: "Outfit" }} {...p} />,
          h2: ({ node, ...p }) => <h2 className="text-base font-bold text-sky-700 dark:text-sky-300 mt-3 mb-1.5 first:mt-0" style={{ fontFamily: "Outfit" }} {...p} />,
          h3: ({ node, ...p }) => <h3 className="text-sm font-bold text-emerald-700 dark:text-emerald-300 mt-2.5 mb-1 first:mt-0" {...p} />,
          h4: ({ node, ...p }) => <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mt-2 mb-1" {...p} />,
          p: ({ node, ...p }) => <p className="my-1.5 leading-relaxed" {...p} />,
          strong: ({ node, ...p }) => <strong className="font-semibold text-slate-900 dark:text-white" {...p} />,
          em: ({ node, ...p }) => <em className="italic text-slate-700 dark:text-slate-200" {...p} />,
          ul: ({ node, ...p }) => <ul className="list-disc pl-5 my-1.5 space-y-1 marker:text-indigo-400" {...p} />,
          ol: ({ node, ...p }) => <ol className="list-decimal pl-5 my-1.5 space-y-1 marker:text-indigo-400 marker:font-semibold" {...p} />,
          li: ({ node, ...p }) => <li className="pl-1" {...p} />,
          blockquote: ({ node, ...p }) => <blockquote className="border-l-4 border-indigo-300 dark:border-indigo-700 pl-3 my-2 italic text-slate-600 dark:text-slate-300 bg-indigo-50/50 dark:bg-indigo-950/30 py-1 rounded-r" {...p} />,
          code: ({ node, inline, ...p }) => inline
            ? <code className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-rose-600 dark:text-rose-300 text-[0.85em] font-mono" {...p} />
            : <code className="block p-3 rounded-lg bg-slate-900 text-slate-100 text-[0.85em] font-mono overflow-x-auto my-2" {...p} />,
          a: ({ node, ...p }) => <a className="text-indigo-600 dark:text-indigo-300 underline" target="_blank" rel="noreferrer" {...p} />,
          hr: () => <hr className="my-3 border-slate-200 dark:border-slate-700" />,
          table: ({ node, ...p }) => <div className="overflow-x-auto my-2"><table className="w-full text-xs border-collapse" {...p} /></div>,
          th: ({ node, ...p }) => <th className="border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-2 py-1 text-left font-semibold" {...p} />,
          td: ({ node, ...p }) => <td className="border border-slate-200 dark:border-slate-700 px-2 py-1" {...p} />,
        }}
      >
        {normalizeMath(content)}
      </ReactMarkdown>
    </div>
  );
}
